/**
 * SMART MEGA MENU V2 - Enhanced JavaScript
 * Handles auto-detection, hover states, mobile behavior
 * Optimized for performance and accessibility
 */

class SmartMegaMenu {
  constructor() {
    this.menu = document.querySelector('.smart-mega-menu');
    if (!this.menu) return;

    this.menuItems = this.menu.querySelectorAll('.mega-menu-item.has-dropdown');
    this.activeItem = null;
    this.hoverTimer = null;
    this.closeTimer = null;
    this.isMobile = this.checkMobile();
    this.isTouch = this.checkTouch();
    
    this.init();
  }

  init() {
    this.setupHoverBehavior();
    this.setupMobileBehavior();
    this.setupAccessibility();
    this.setupOutsideClick();
    this.setupResize();
    this.detectActiveMenuItem();
    
    // Performance: Use passive listeners where possible
    this.setupPassiveListeners();
  }

  checkMobile() {
    return window.innerWidth <= 768;
  }

  checkTouch() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Desktop Hover Behavior
   */
  setupHoverBehavior() {
    if (this.isMobile) return;

    this.menuItems.forEach(item => {
      // Mouse enter with small delay for better UX
      item.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimer);
        
        this.hoverTimer = setTimeout(() => {
          this.closeAllDropdowns();
          this.openDropdown(item);
        }, 50);
      });

      // Mouse leave with delay to prevent accidental closes
      item.addEventListener('mouseleave', () => {
        clearTimeout(this.hoverTimer);
        
        this.closeTimer = setTimeout(() => {
          if (!item.matches(':hover')) {
            this.closeDropdown(item);
          }
        }, 150);
      });

      // Keep dropdown open when hovering over it
      const dropdown = item.querySelector('.mega-menu-dropdown');
      if (dropdown) {
        dropdown.addEventListener('mouseenter', () => {
          clearTimeout(this.closeTimer);
        });

        dropdown.addEventListener('mouseleave', () => {
          this.closeTimer = setTimeout(() => {
            this.closeDropdown(item);
          }, 150);
        });
      }
    });
  }

  /**
   * Mobile Touch/Click Behavior
   */
  setupMobileBehavior() {
    if (!this.isMobile) return;

    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu-link');
      
      link.addEventListener('click', (e) => {
        // Prevent default if item has dropdown
        if (item.classList.contains('has-dropdown')) {
          const isActive = item.classList.contains('active');
          
          // If dropdown is already open, allow navigation to the link
          // If link is '#' or empty, always prevent and toggle
          if (!isActive || link.getAttribute('href') === '#' || link.getAttribute('href') === '') {
            e.preventDefault();
            this.toggleMobileDropdown(item);
          }
        }
      });
    });
  }

  /**
   * Toggle Mobile Dropdown
   */
  toggleMobileDropdown(item) {
    const isActive = item.classList.contains('active');
    
    // Close all other dropdowns
    this.closeAllDropdowns();
    
    // Toggle current dropdown
    if (!isActive) {
      this.openDropdown(item);
    }
  }

  /**
   * Open Dropdown
   */
  openDropdown(item) {
    item.classList.add('active');
    const dropdown = item.querySelector('.mega-menu-dropdown');
    
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');
      
      // Set max-height for mobile animation
      if (this.isMobile) {
        const height = dropdown.scrollHeight;
        dropdown.style.maxHeight = height + 'px';
      }
    }
    
    this.activeItem = item;
    
    // Emit custom event for analytics
    this.menu.dispatchEvent(new CustomEvent('megamenu:open', {
      detail: { item: item, title: item.querySelector('.mega-menu-link').textContent }
    }));
  }

  /**
   * Close Dropdown
   */
  closeDropdown(item) {
    item.classList.remove('active');
    const dropdown = item.querySelector('.mega-menu-dropdown');
    
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
      
      // Reset max-height for mobile
      if (this.isMobile) {
        dropdown.style.maxHeight = '0';
      }
    }
    
    if (this.activeItem === item) {
      this.activeItem = null;
    }
  }

  /**
   * Close All Dropdowns
   */
  closeAllDropdowns() {
    this.menuItems.forEach(item => {
      this.closeDropdown(item);
    });
  }

  /**
   * Setup Outside Click Handler
   */
  setupOutsideClick() {
    document.addEventListener('click', (e) => {
      if (!this.menu.contains(e.target) && this.activeItem) {
        this.closeAllDropdowns();
      }
    });
  }

  /**
   * Keyboard Accessibility
   */
  setupAccessibility() {
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu-link');
      
      link.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'Enter':
          case ' ':
            // Space or Enter opens dropdown on mobile, or follows link on desktop
            if (this.isMobile && item.classList.contains('has-dropdown')) {
              e.preventDefault();
              this.toggleMobileDropdown(item);
            }
            break;
            
          case 'Escape':
            e.preventDefault();
            this.closeDropdown(item);
            link.focus();
            break;
            
          case 'ArrowDown':
            // Focus first link in dropdown
            if (item.classList.contains('active')) {
              e.preventDefault();
              const firstLink = item.querySelector('.mega-menu-links a');
              if (firstLink) firstLink.focus();
            }
            break;
            
          case 'ArrowRight':
            // Move to next menu item
            e.preventDefault();
            this.focusNextMenuItem(item);
            break;
            
          case 'ArrowLeft':
            // Move to previous menu item
            e.preventDefault();
            this.focusPrevMenuItem(item);
            break;
        }
      });

      // Keyboard navigation within dropdowns
      const dropdownLinks = item.querySelectorAll('.mega-menu-links a');
      dropdownLinks.forEach((dropdownLink, index) => {
        dropdownLink.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowUp' && index > 0) {
            e.preventDefault();
            dropdownLinks[index - 1].focus();
          } else if (e.key === 'ArrowDown' && index < dropdownLinks.length - 1) {
            e.preventDefault();
            dropdownLinks[index + 1].focus();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this.closeDropdown(item);
            link.focus();
          }
        });
      });
    });
  }

  /**
   * Focus Next Menu Item
   */
  focusNextMenuItem(currentItem) {
    const items = Array.from(this.menuItems);
    const currentIndex = items.indexOf(currentItem);
    const nextIndex = (currentIndex + 1) % items.length;
    const nextLink = items[nextIndex].querySelector('.mega-menu-link');
    if (nextLink) nextLink.focus();
  }

  /**
   * Focus Previous Menu Item
   */
  focusPrevMenuItem(currentItem) {
    const items = Array.from(this.menuItems);
    const currentIndex = items.indexOf(currentItem);
    const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    const prevLink = items[prevIndex].querySelector('.mega-menu-link');
    if (prevLink) prevLink.focus();
  }

  /**
   * Detect and Mark Active Menu Item (Current Page)
   */
  detectActiveMenuItem() {
    const currentPath = window.location.pathname;
    
    this.menuItems.forEach(item => {
      const link = item.querySelector('.mega-menu-link');
      const linkPath = new URL(link.href).pathname;
      
      // Check if this is the current page
      if (linkPath === currentPath) {
        item.classList.add('active-page');
        link.setAttribute('aria-current', 'page');
      }
      
      // Check dropdown links
      const dropdownLinks = item.querySelectorAll('.mega-menu-links a');
      dropdownLinks.forEach(dropdownLink => {
        const dropdownPath = new URL(dropdownLink.href).pathname;
        if (dropdownPath === currentPath) {
          item.classList.add('active-page');
        }
      });
    });
  }

  /**
   * Handle Window Resize
   */
  setupResize() {
    let resizeTimer;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = this.checkMobile();
        
        // If switching between mobile/desktop, reset everything
        if (wasMobile !== this.isMobile) {
          this.closeAllDropdowns();
          this.reinitialize();
        }
      }, 250);
    });
  }

  /**
   * Reinitialize (after resize)
   */
  reinitialize() {
    // Remove all event listeners by cloning
    const oldMenu = this.menu;
    const newMenu = oldMenu.cloneNode(true);
    oldMenu.parentNode.replaceChild(newMenu, oldMenu);
    
    // Reinitialize with new menu
    this.menu = newMenu;
    this.menuItems = this.menu.querySelectorAll('.mega-menu-item.has-dropdown');
    this.init();
  }

  /**
   * Setup Passive Event Listeners for Performance
   */
  setupPassiveListeners() {
    // Add passive scroll listener if needed for scroll-based behavior
    window.addEventListener('scroll', () => {
      // Could add sticky menu behavior here if needed
    }, { passive: true });
  }

  /**
   * Public API: Programmatically open a menu item
   */
  open(menuItemTitle) {
    const item = Array.from(this.menuItems).find(item => {
      const link = item.querySelector('.mega-menu-link');
      return link && link.textContent.trim() === menuItemTitle;
    });
    
    if (item) {
      this.closeAllDropdowns();
      this.openDropdown(item);
    }
  }

  /**
   * Public API: Programmatically close all dropdowns
   */
  close() {
    this.closeAllDropdowns();
  }
}

// Initialize when DOM is ready
function initSmartMegaMenu() {
  const megaMenu = new SmartMegaMenu();
  
  // Expose to window for external access if needed
  window.smartMegaMenu = megaMenu;
  
  // Analytics integration example
  document.querySelector('.smart-mega-menu')?.addEventListener('megamenu:open', (e) => {
    // Track mega menu opens in your analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'mega_menu_open', {
        'menu_item': e.detail.title
      });
    }
  });
}

// Initialize based on document state
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSmartMegaMenu);
} else {
  initSmartMegaMenu();
}

// Reinitialize when Shopify sections are reloaded (theme editor)
document.addEventListener('shopify:section:load', initSmartMegaMenu);
document.addEventListener('shopify:section:reorder', initSmartMegaMenu);

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SmartMegaMenu;
}
